import {
  ArrowRightOutlined,
  CameraOutlined,
  FilePdfOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  BILL_STATUS,
  BillDetails,
  billsCount,
  describeError,
  dirhams,
  fullDate,
  IdTag,
  type MonthKey,
  monthLabel,
  present,
  receiptsCount,
  WHATSAPP_STATUS_COLORS,
  WHATSAPP_STATUS_LABELS,
  type WhatsappStatus,
} from '@horof/shared';
import {
  Alert,
  Button,
  Input,
  Select,
  Skeleton,
  Table,
  type TableProps,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from 'urql';
import {
  BillPageDocument,
  type BillRow,
  BillsDocument,
  billingContext,
  PaymentDocument,
  type PaymentRow,
  PaymentsDocument,
  ReceiversDocument,
} from './billing-data';
import {
  BILL_FILTER_KEYS,
  billsFilter,
  PAYMENT_FILTER_KEYS,
  paymentsFilter,
} from './billing-filter';
import { PeriodFilter } from './PeriodFilter';
import { DeskPanel } from './panel';
import { useFitColumns } from './table-fit';
import { BILL_TONE, ListHeader, PageHeader, Status } from './ui';

const PAGE_SIZES = [25, 50, 100];

/** A list's filter, with what its page fixes (a member, a meter). */
function withFixed(
  filter: { and: Record<string, unknown>[] } | null,
  fixed: (Record<string, unknown> | false | undefined | '')[],
) {
  const extra = fixed.filter((f): f is Record<string, unknown> => !!f);
  if (!extra.length) return filter;
  return { and: [...(filter?.and ?? []), ...extra] };
}

/** Without the columns a page already says (its member, its meter). */
const hideColumns =
  (...keys: (string | false | undefined | '')[]) =>
  <C extends { key?: unknown }>(columns: C[]) =>
    columns.filter((c) => !keys.includes(c.key as string));
const month = (d: string) => monthLabel(d.slice(0, 7) as MonthKey);

/** Filters, page, size and sort in the address, as in the operations. */
function useListParams(keys: readonly string[]) {
  const [params, setParams] = useSearchParams();
  const update = useCallback(
    (patch: Record<string, string | null>, resetPage = true) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          if (resetPage) next.delete('page');
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const page = Number(params.get('page') ?? 1) || 1;
  const size = PAGE_SIZES.includes(Number(params.get('size')))
    ? Number(params.get('size'))
    : 50;
  const filtered = keys.some((k) => params.get(k));
  const clear = () => update(Object.fromEntries(keys.map((k) => [k, null])));
  return { params, update, page, size, filtered, clear };
}

function pagination(
  page: number,
  size: number,
  total: number,
): TableProps<unknown>['pagination'] {
  return {
    current: page,
    pageSize: size,
    total,
    size: 'small',
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZES,
    showTotal: (n, [a, b]) => `${a}–${b} من ${n}`,
  };
}

// ——— Bills ———

const BILL_SORTS = {
  'month-desc': ['BILL_MONTH_DESC', 'CREATED_AT_DESC'],
  'month-asc': ['BILL_MONTH_ASC', 'CREATED_AT_ASC'],
  'total-desc': ['BILL_TOTAL_DESC', 'BILL_MONTH_DESC'],
  'total-asc': ['BILL_TOTAL_ASC', 'BILL_MONTH_DESC'],
} as const;
type BillSort = keyof typeof BILL_SORTS;

/**
 * «الفواتير»: every bill, paged by arqam, filtered in the address (month or
 * range, status, member or meter, with or without a photo). A row opens the
 * bill's page.
 */
export function BillsScreen() {
  return (
    <section className="desk-page">
      <BillsList
        title="الفواتير"
        description="كل الفواتير كما أصدرها arqam، الأحدث أولاً. تُفتح الفاتورة في صفحتها."
      />
    </section>
  );
}

/**
 * The bills' list, filters and table: on «الفواتير», and in a member's or a
 * meter's page with that member or meter fixed (doc 58).
 */
export function BillsList({
  fixed,
  title,
  description,
}: {
  fixed?: { member?: string; meter?: string };
  /** On its own page: the header with the count (doc 63). */
  title?: string;
  description?: string;
}) {
  const navigate = useNavigate();
  const { ref: fitRef, fit } = useFitColumns<HTMLDivElement>();
  const { params, update, page, size, filtered, clear } =
    useListParams(BILL_FILTER_KEYS);
  const sortParam = params.get('sort');
  const sort: BillSort =
    sortParam && sortParam in BILL_SORTS
      ? (sortParam as BillSort)
      : 'month-desc';
  const filter = useMemo(
    () =>
      withFixed(billsFilter(params), [
        fixed?.member && { memberId: { equalTo: fixed.member } },
        fixed?.meter && { meterId: { equalTo: fixed.meter } },
      ]),
    [params, fixed?.member, fixed?.meter],
  );
  const visible = hideColumns(
    fixed?.member && 'member',
    fixed?.meter && 'meter',
  );
  const [{ data, error, fetching }] = useQuery({
    query: BillsDocument,
    variables: {
      filter,
      orderBy: [...BILL_SORTS[sort]],
      first: size,
      offset: (page - 1) * size,
    },
    context: useMemo(() => billingContext, []),
  });
  const rows = present(data?.bills?.nodes);
  const total = data?.bills?.totalCount ?? 0;
  const order = (key: 'month' | 'total') =>
    sort.startsWith(key)
      ? sort.endsWith('asc')
        ? ('ascend' as const)
        : ('descend' as const)
      : null;
  const scope = fixed
    ? null
    : params.get('member')
      ? 'member'
      : params.get('meter')
        ? 'meter'
        : null;

  return (
    <div className="desk-list-block" ref={fitRef}>
      {title && (
        <ListHeader
          title={title}
          count={data ? total : undefined}
          description={description}
        />
      )}
      <div className="ui-table-card">
        <div className="desk-toolbar">
          <PeriodFilter
            key={params.get('from') || params.get('to') ? 'set' : 'none'}
            from={params.get('from')}
            to={params.get('to')}
            onChange={(from, to) => update({ from, to })}
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="العضو أو العداد"
            className="desk-search"
            value={params.get('q') ?? ''}
            onChange={(e) => update({ q: e.target.value })}
          />
          <Select
            allowClear
            placeholder="الحالة"
            className="desk-filter"
            value={params.get('status') ?? undefined}
            onChange={(v) => update({ status: v ?? null })}
            options={[
              { value: 'unpaid', label: 'غير مؤداة (كلها)' },
              { value: 'PENDING', label: BILL_STATUS.PENDING.label },
              { value: 'OVERDUE', label: BILL_STATUS.OVERDUE.label },
              { value: 'PAID', label: BILL_STATUS.PAID.label },
            ]}
          />
          <Select
            allowClear
            placeholder="صورة العداد"
            className="desk-filter"
            value={params.get('photo') ?? undefined}
            onChange={(v) => update({ photo: v ?? null })}
            options={[
              { value: 'with', label: 'مع صورة' },
              { value: 'without', label: 'بلا صورة' },
            ]}
          />
          {filtered && (
            <Button type="link" onClick={clear}>
              إلغاء التصفية
            </Button>
          )}
        </div>
        {scope && rows[0] && (
          <Alert
            type="info"
            showIcon
            message={
              scope === 'member'
                ? `فواتير العضو ${rows[0].member?.fullName ?? ''}`
                : `فواتير العداد ${rows[0].meter?.label ?? ''}`
            }
            action={
              <Button
                size="small"
                onClick={() => update({ member: null, meter: null })}
              >
                كل الفواتير
              </Button>
            }
          />
        )}
        {!title && (
          <div className="desk-count">
            <span>{data ? billsCount(total) : '…'}</span>
          </div>
        )}
        {error && (
          <Alert
            type="error"
            showIcon
            message={describeError(error, 'تعذّر تحميل الفواتير.')}
          />
        )}
        <Table<BillRow>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          dataSource={rows}
          loading={fetching && !data}
          scroll={{ y: 'calc(100vh - 320px)' }}
          pagination={pagination(page, size, total)}
          sortDirections={['descend', 'ascend']}
          showSorterTooltip={false}
          onChange={(p, _f, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const field = s?.columnKey === 'total' ? 'total' : 'month';
            const next: BillSort = s?.order
              ? (`${field}-${s.order === 'ascend' ? 'asc' : 'desc'}` as BillSort)
              : 'month-desc';
            update(
              {
                sort: next === 'month-desc' ? null : next,
                size: p.pageSize === 50 ? null : String(p.pageSize),
                page:
                  next !== sort || p.current === 1 ? null : String(p.current),
              },
              false,
            );
          }}
          rowClassName={() => 'is-clickable'}
          onRow={(b) => ({ onClick: () => navigate(`/bills/${b.id}`) })}
          locale={{ emptyText: 'لا فواتير بهذا الاختيار' }}
          columns={fit<BillRow>(
            visible([
              {
                key: 'month',
                title: 'الشهر',
                width: 120,
                sorter: true,
                sortOrder: order('month'),
                render: (_, b) => month(b.billMonth),
              },
              {
                key: 'member',
                title: 'العضو',
                ellipsis: true,
                render: (_, b) => b.member?.fullName,
              },
              {
                key: 'meter',
                title: 'العداد',
                width: 150,
                ellipsis: true,
                drop: 4,
                render: (_, b) => b.meter?.label,
              },
              {
                key: 'reading',
                title: 'القراءة',
                width: 130,
                drop: 3,
                render: (_, b) => (
                  <span className="ltr-num">
                    {b.previousReading} → {b.currentReading}
                  </span>
                ),
              },
              {
                key: 'consumption',
                title: 'الاستهلاك',
                width: 90,
                drop: 2,
                render: (_, b) => (
                  <span className="ltr-num">{b.consumption} م³</span>
                ),
              },
              {
                key: 'total',
                title: 'المبلغ',
                width: 120,
                sorter: true,
                sortOrder: order('total'),
                render: (_, b) => <strong>{dirhams(b.billTotal)}</strong>,
              },
              {
                key: 'remaining',
                title: 'الباقي',
                width: 120,
                drop: 5,
                render: (_, b) =>
                  Number(b.remainingAmount) ? (
                    <span className="is-debit">
                      {dirhams(b.remainingAmount)}
                    </span>
                  ) : (
                    <span className="field-hint">—</span>
                  ),
              },
              {
                key: 'status',
                title: 'الحالة',
                width: 100,
                render: (_, b) => (
                  <Status tone={BILL_TONE[b.billStatus]}>
                    {BILL_STATUS[b.billStatus].label}
                  </Status>
                ),
              },
              {
                key: 'photo',
                title: 'صورة',
                width: 64,
                drop: 1,
                render: (_, b) =>
                  b.readingPhotoId ? (
                    <CameraOutlined className="receipt-mark" />
                  ) : null,
              },
            ]),
          )}
        />
      </div>
    </div>
  );
}

/**
 * A bill's page: the shared details (reading and photo, breakdown,
 * payments, PDFs), the WhatsApp messages sent about it, and links to the
 * member's and the meter's other bills.
 */
export function BillScreen() {
  const { billId = '' } = useParams();
  const navigate = useNavigate();
  const [{ data, error }] = useQuery({
    query: BillPageDocument,
    variables: { id: billId },
    context: useMemo(() => billingContext, []),
  });
  const bill = data?.bill;
  const messages = present(bill?.whatsappMessages.nodes);

  return (
    <section className="desk-page desk-bill-page">
      <PageHeader
        breadcrumbs={[
          { label: 'الفواتير', to: '/bills' },
          ...(bill?.member
            ? [
                {
                  label: bill.member.fullName ?? 'العضو',
                  to: `/members/${bill.member.id}`,
                },
              ]
            : []),
        ]}
        title={bill ? `فاتورة ${month(bill.billMonth)}` : 'الفاتورة'}
        actions={
          bill && (
            <>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(-1)}
              >
                رجوع
              </Button>
              {bill.member && (
                <Link to={`/bills?member=${bill.member.id}`}>
                  <Button>كل فواتير العضو</Button>
                </Link>
              )}
              {bill.meter && (
                <Link to={`/bills?meter=${bill.meter.id}`}>
                  <Button>كل فواتير العداد</Button>
                </Link>
              )}
            </>
          )
        }
      />
      {error && (
        <Alert
          type="error"
          showIcon
          message={describeError(error, 'تعذّر تحميل الفاتورة.')}
        />
      )}
      {!data && !error && <Skeleton active paragraph={{ rows: 10 }} />}
      {data && (
        <div className="desk-bill-grid">
          <div className="desk-bill-main">
            <BillDetails
              billId={billId}
              memberLink={(id) => `/members/${id}`}
              meterLink={(id) => `/meters/${id}`}
            />
          </div>
          {bill && (
            <div className="desk-bill-side">
              <section className="desk-card">
                <h2>واتساب</h2>
                {messages.length === 0 ? (
                  <div className="field-hint">
                    لم تُرسل رسالة عن هذه الفاتورة.
                  </div>
                ) : (
                  <ul className="desk-messages">
                    {messages.map((m) => (
                      <li key={m.id}>
                        <Tag
                          color={
                            WHATSAPP_STATUS_COLORS[m.status as WhatsappStatus]
                          }
                        >
                          {WHATSAPP_STATUS_LABELS[m.status as WhatsappStatus] ??
                            m.status}
                        </Tag>
                        <span className="ltr-num">{m.toPhone}</span>
                        <div className="field-hint">
                          {fullDate(m.sentAt ?? m.createdAt)} ·{' '}
                          {dayjs(m.sentAt ?? m.createdAt).format('HH:mm')}
                          {m.queuedBy && ` · ${m.queuedBy}`}
                        </div>
                        {m.error && (
                          <div className="field-error">{m.error}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section className="desk-card">
                <h2>السجل</h2>
                <div className="field-hint">
                  أُنشئت {fullDate(bill.createdAt)}
                  {bill.updatedAt !== bill.createdAt &&
                    ` · عُدِّلت ${fullDate(bill.updatedAt)}`}
                </div>
                <IdTag id={bill.id} label="معرّف الفاتورة" link />
              </section>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ——— Payments ———

const PAYMENT_SORTS = {
  'date-desc': ['PAYMENT_DATE_DESC', 'CREATED_AT_DESC'],
  'date-asc': ['PAYMENT_DATE_ASC', 'CREATED_AT_ASC'],
  'amount-desc': ['AMOUNT_DESC', 'PAYMENT_DATE_DESC'],
  'amount-asc': ['AMOUNT_ASC', 'PAYMENT_DATE_DESC'],
} as const;
type PaymentSort = keyof typeof PAYMENT_SORTS;

/**
 * «الأداءات»: every payment, paged by arqam: by period, receipt number or
 * member, and who received it. A row opens the payment in a drawer.
 */
export function PaymentsScreen() {
  return (
    <section className="desk-page">
      <PaymentsList
        title="الأداءات"
        description="كل الأداءات المسجَّلة، الأحدث أولاً. يُفتح الأداء فوق القائمة."
      />
    </section>
  );
}

/** The payments' list: on «الأداءات», and in a member's page (doc 58). */
export function PaymentsList({
  fixed,
  title,
  description,
}: {
  fixed?: { member?: string };
  /** On its own page: the header with the count (doc 63). */
  title?: string;
  description?: string;
}) {
  const { ref: fitRef, fit } = useFitColumns<HTMLDivElement>();
  const { params, update, page, size, filtered, clear } =
    useListParams(PAYMENT_FILTER_KEYS);
  const sortParam = params.get('sort');
  const sort: PaymentSort =
    sortParam && sortParam in PAYMENT_SORTS
      ? (sortParam as PaymentSort)
      : 'date-desc';
  const filter = useMemo(
    () =>
      withFixed(paymentsFilter(params), [
        fixed?.member && { bill: { memberId: { equalTo: fixed.member } } },
      ]),
    [params, fixed?.member],
  );
  const visible = hideColumns(fixed?.member && 'member');
  const context = useMemo(() => billingContext, []);
  const [{ data, error, fetching }] = useQuery({
    query: PaymentsDocument,
    variables: {
      filter,
      orderBy: [...PAYMENT_SORTS[sort]],
      first: size,
      offset: (page - 1) * size,
    },
    context,
  });
  const [{ data: receivers }] = useQuery({ query: ReceiversDocument, context });
  const people = [
    ...new Set(
      present(receivers?.payments?.nodes).flatMap((p) =>
        p.receivedBy ? [p.receivedBy] : [],
      ),
    ),
  ].sort();
  const rows = present(data?.payments?.nodes);
  const total = data?.payments?.totalCount ?? 0;
  const panel = params.get('panel');
  const order = (key: 'date' | 'amount') =>
    sort.startsWith(key)
      ? sort.endsWith('asc')
        ? ('ascend' as const)
        : ('descend' as const)
      : null;

  return (
    <div className="desk-list-block" ref={fitRef}>
      {title && (
        <ListHeader
          title={title}
          count={data ? total : undefined}
          description={description}
        />
      )}
      <div className="ui-table-card">
        <div className="desk-toolbar">
          <PeriodFilter
            key={params.get('from') || params.get('to') ? 'set' : 'none'}
            from={params.get('from')}
            to={params.get('to')}
            onChange={(from, to) => update({ from, to })}
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="رقم الوصل أو العضو"
            className="desk-search"
            value={params.get('q') ?? ''}
            onChange={(e) => update({ q: e.target.value })}
          />
          {people.length > 0 && (
            <Select
              allowClear
              showSearch
              placeholder="استلمها"
              className="desk-filter"
              value={params.get('by') ?? undefined}
              onChange={(v) => update({ by: v ?? null })}
              options={people.map((p) => ({ value: p, label: p }))}
            />
          )}
          {filtered && (
            <Button type="link" onClick={clear}>
              إلغاء التصفية
            </Button>
          )}
        </div>
        {!title && (
          <div className="desk-count">
            <span>{data ? receiptsCount(total) : '…'}</span>
          </div>
        )}
        {error && (
          <Alert
            type="error"
            showIcon
            message={describeError(error, 'تعذّر تحميل الأداءات.')}
          />
        )}
        <Table<PaymentRow>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          dataSource={rows}
          loading={fetching && !data}
          scroll={{ y: 'calc(100vh - 320px)' }}
          pagination={pagination(page, size, total)}
          sortDirections={['descend', 'ascend']}
          showSorterTooltip={false}
          onChange={(p, _f, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const field = s?.columnKey === 'amount' ? 'amount' : 'date';
            const next: PaymentSort = s?.order
              ? (`${field}-${s.order === 'ascend' ? 'asc' : 'desc'}` as PaymentSort)
              : 'date-desc';
            update(
              {
                sort: next === 'date-desc' ? null : next,
                size: p.pageSize === 50 ? null : String(p.pageSize),
                page:
                  next !== sort || p.current === 1 ? null : String(p.current),
              },
              false,
            );
          }}
          rowClassName={(p) =>
            p.id === panel ? 'is-open is-clickable' : 'is-clickable'
          }
          onRow={(p) => ({ onClick: () => update({ panel: p.id }, false) })}
          locale={{ emptyText: 'لا أداءات بهذا الاختيار' }}
          columns={fit<PaymentRow>(
            visible([
              {
                key: 'date',
                title: 'التاريخ',
                width: 110,
                sorter: true,
                sortOrder: order('date'),
                render: (_, p) => (
                  <span className="ltr-num">
                    {dayjs(p.paymentDate).format('YYYY-MM-DD')}
                  </span>
                ),
              },
              {
                key: 'receipt',
                title: 'الوصل',
                width: 120,
                render: (_, p) => (
                  <span className="ltr-num">{p.receiptNo}</span>
                ),
              },
              {
                key: 'member',
                title: 'العضو',
                ellipsis: true,
                render: (_, p) => p.bill?.member?.fullName,
              },
              {
                key: 'bill',
                title: 'الفاتورة',
                width: 130,
                drop: 2,
                render: (_, p) => (p.bill ? month(p.bill.billMonth) : null),
              },
              {
                key: 'amount',
                title: 'المبلغ',
                width: 120,
                sorter: true,
                sortOrder: order('amount'),
                render: (_, p) => <strong>{dirhams(p.amount)}</strong>,
              },
              {
                key: 'by',
                title: 'استلمها',
                width: 140,
                ellipsis: true,
                drop: 1,
                render: (_, p) => p.receivedBy,
              },
            ]),
          )}
        />
        {panel && (
          <PaymentDrawer
            key={panel}
            id={panel}
            onClose={() => update({ panel: null }, false)}
          />
        )}
      </div>
    </div>
  );
}

/** One payment, over the list (doc 57): it has nothing nested to list. */
function PaymentDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [{ data, error }] = useQuery({
    query: PaymentDocument,
    variables: { id },
    context: useMemo(() => billingContext, []),
  });
  const p = data?.payment;
  return (
    <DeskPanel
      onClose={onClose}
      recordId={p?.id}
      title={
        p ? (
          <span>
            الوصل <span className="ltr-num">{p.receiptNo}</span>
          </span>
        ) : (
          'الأداء'
        )
      }
    >
      {error && <Alert type="error" showIcon message="تعذّر تحميل الأداء" />}
      {!data && !error && <Skeleton active paragraph={{ rows: 6 }} />}
      {data && !p && (
        <Alert type="warning" showIcon message="الأداء غير موجود" />
      )}
      {p && (
        <div className="desk-drawer-body">
          <dl className="desk-facts">
            <dt>المبلغ</dt>
            <dd>
              <strong>{dirhams(p.amount)}</strong>
            </dd>
            <dt>التاريخ</dt>
            <dd>
              {fullDate(p.paymentDate)} · {dayjs(p.paymentDate).format('HH:mm')}
            </dd>
            <dt>استلمها</dt>
            <dd>{p.receivedBy ?? '—'}</dd>
            {p.bill?.member && (
              <>
                <dt>العضو</dt>
                <dd>
                  <Link to={`/members/${p.bill.member.id}`}>
                    {p.bill.member.fullName}
                  </Link>
                </dd>
              </>
            )}
            {p.bill?.meter && (
              <>
                <dt>العداد</dt>
                <dd>
                  <Link to={`/meters/${p.bill.meter.id}`}>
                    {p.bill.meter.label}
                  </Link>
                </dd>
              </>
            )}
          </dl>
          {p.bill && (
            <section className="desk-card">
              <h2>
                فاتورة {month(p.bill.billMonth)}{' '}
                <Tag color={BILL_STATUS[p.bill.billStatus].color}>
                  {BILL_STATUS[p.bill.billStatus].label}
                </Tag>
              </h2>
              <div>
                المبلغ {dirhams(p.bill.billTotal)}
                {Number(p.bill.remainingAmount) > 0 &&
                  ` · الباقي ${dirhams(p.bill.remainingAmount)}`}
              </div>
              <Link to={`/bills/${p.bill.id}`}>
                <Button block>فتح الفاتورة</Button>
              </Link>
            </section>
          )}
          <Button
            icon={<FilePdfOutlined />}
            href={p.receiptLink}
            target="_blank"
            block
          >
            الوصل (PDF)
          </Button>
        </div>
      )}
    </DeskPanel>
  );
}
